import { useState, useEffect } from "react";
import { openPaywall } from "../components/Paywall";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import BottomNav from "../components/BottomNav";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Heart, Star, Lock, MapPin, Check, MessageCircle, Unlock, Coins } from "lucide-react";

import { API } from "../config";
import { tx } from "../lib/tx";

export default function WhoLikedMe() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [receivedLikes, setReceivedLikes] = useState([]);
  const [sentLikes, setSentLikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("received");
  const [unlockCost, setUnlockCost] = useState(5);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    fetchLikes();
    fetchSentLikes();
  }, []);

  const fetchLikes = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/likes/received`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReceivedLikes(data.likes || []);
        setUnlockCost(data.unlock_cost || 5);
      }
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSentLikes = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/likes/sent`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSentLikes(data.likes || []);
      }
    } catch (error) {
      console.error("Fetch sent likes error:", error);
    }
  };

  const handleUnlockSingle = async (likeId, buttonElement) => {
    // Get credits directly from localStorage to avoid React context
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    const currentCredits = storedUser.credits || 0;
    
    if (currentCredits < unlockCost && !storedUser.isPremium) {
      openPaywall("likes");
      return;
    }

    // Disable button directly via DOM to avoid React re-render
    if (buttonElement) {
      buttonElement.disabled = true;
      buttonElement.innerHTML = "Chargement...";
    }
    
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/likes/unlock/${likeId}`, {
        method: "POST",
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (response.ok) {
        // Force reload immediately without any React state updates
        window.location.href = window.location.href;
      } else {
        const data = await response.json();
        if (response.status !== 402) alert(data.detail || tx("Erreur lors du déblocage"));
        if (buttonElement) {
          buttonElement.disabled = false;
          buttonElement.innerHTML = `<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 11V7a4 4 0 1 1 8 0m-9 4h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2z"/></svg> ${tx("Débloquer (")}${unlockCost} ${tx("crédits)")}`;
        }
      }
    } catch (error) {
      console.error("Unlock error:", error);
      alert(tx("Erreur de connexion"));
      if (buttonElement) {
        buttonElement.disabled = false;
        buttonElement.innerHTML = `${tx("Débloquer (")}${unlockCost} ${tx("crédits)")}`;
      }
    }
  };

  const handleRevealAll = async () => {
    const BULK_COST = 20;
    const lockedCount = receivedLikes.filter(l => !l.is_unlocked).length;
    
    if (lockedCount === 0) {
      alert(tx("Tous les profils sont déjà débloqués"));
      return;
    }
    
    if ((user?.credits || 0) < BULK_COST && !user?.isPremium) {
      openPaywall("likes");
      return;
    }

    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/likes/reveal`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        window.location.reload();
      } else if (response.status !== 402) {
        const data = await response.json();
        alert(data.detail || tx("Erreur"));
      }
    } catch (error) {
      console.error("Reveal all error:", error);
      alert(tx("Erreur lors du déblocage"));
    }
  };

  const handleLikeBack = async (targetUserId) => {
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/likes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          target_user_id: targetUserId,
          action_type: "like"
        })
      });

      const data = await response.json();

      if (data.is_match) {
        setSuccessMessage(tx("C'est un Match ! 🎉"));
      } else {
        setSuccessMessage(tx("Like envoyé !"));
      }
      
      // Clear message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
      
      setReceivedLikes(prev => prev.filter(l => l.user.user_id !== targetUserId));
    } catch (error) {
      console.error("Like error:", error);
      alert(tx("Erreur"));
    }
  };

  const lockedCount = receivedLikes.filter(l => !l.is_unlocked).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-safe" data-testid="who-liked-me-page">
      
      <div className="pt-6 pb-24 px-4">
        <div className="max-w-lg mx-auto">
          <h1 className="font-heading text-3xl font-bold text-white mb-2">
            {tx("Mes Likes")}
          </h1>
          
          {/* Success message */}
          {successMessage && (
            <div className="bg-green-500/20 border border-green-500/50 text-green-400 px-4 py-2 rounded-lg mb-4 text-center">
              {successMessage}
            </div>
          )}
          
          {/* Credits indicator */}
          <div className="flex items-center gap-2 mb-6 text-muted-foreground">
            <Coins className="w-4 h-4 text-yellow-500" />
            <span className="text-sm">{user?.credits || 0} {tx("crédits disponibles")}</span>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full bg-card/50 border border-white/5 rounded-full p-1 mb-6">
              <TabsTrigger 
                value="received" 
                className="flex-1 rounded-full data-[state=active]:bg-primary data-[state=active]:text-white"
              >
                <Heart className="w-4 h-4 mr-2" />
                {tx("Reçus (")}{receivedLikes.length})
              </TabsTrigger>
              <TabsTrigger 
                value="sent"
                className="flex-1 rounded-full data-[state=active]:bg-primary data-[state=active]:text-white"
              >
                <Heart className="w-4 h-4 mr-2 fill-current" />
                {tx("Envoyés (")}{sentLikes.length})
              </TabsTrigger>
            </TabsList>

            {/* Received Likes Tab */}
            <TabsContent value="received">
              {/* Bulk Unlock Option */}
              {lockedCount > 1 && (
                <div className="bg-gradient-to-r from-primary/20 to-purple-600/20 border border-primary/30 rounded-2xl p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">
                        {lockedCount} {tx("profils masqués")}
                      </p>
                      <p className="text-muted-foreground text-sm">
                        {tx("Débloquez tout d'un coup pour 20 crédits")}
                      </p>
                    </div>
                    <Button
                      onClick={handleRevealAll}
                      className="bg-primary text-white rounded-full font-bold"
                      size="sm"
                    >
                      <Unlock className="w-4 h-4 mr-1" />
                      {tx("Tout voir")}
                    </Button>
                  </div>
                </div>
              )}

              {/* Received Likes List */}
              {receivedLikes.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-24 h-24 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-6">
                    <Heart className="w-12 h-12 text-muted-foreground" />
                  </div>
                  <h3 className="font-heading text-xl text-white mb-2">{tx("Pas encore de likes")}</h3>
                  <p className="text-muted-foreground">
                    {tx("Complétez votre profil pour recevoir plus de likes !")}
                  </p>
                  <Button
                    onClick={() => navigate("/profile/edit")}
                    className="mt-4 bg-secondary text-black rounded-full font-bold"
                  >
                    {tx("Améliorer mon profil")}
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {receivedLikes.map(({ like, user: likeUser, is_unlocked }) => (
                    <LikeCard
                      key={`${like.like_id}-${is_unlocked}`}
                      like={like}
                      likeUser={likeUser}
                      isUnlocked={is_unlocked}
                      unlockCost={unlockCost}
                      onUnlock={handleUnlockSingle}
                      onLikeBack={() => handleLikeBack(likeUser.user_id)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Sent Likes Tab */}
            <TabsContent value="sent">
              {sentLikes.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-24 h-24 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-6">
                    <Heart className="w-12 h-12 text-muted-foreground" />
                  </div>
                  <h3 className="font-heading text-xl text-white mb-2">{tx("Aucun like envoyé")}</h3>
                  <p className="text-muted-foreground">
                    {tx("Découvrez des profils et likez ceux qui vous plaisent !")}
                  </p>
                  <Button
                    onClick={() => navigate("/discover")}
                    className="mt-4 bg-primary text-white rounded-full font-bold"
                  >
                    {tx("Découvrir")}
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {sentLikes.map(({ like, user: likeUser, is_match }) => (
                    <SentLikeCard
                      key={like.like_id}
                      like={like}
                      likeUser={likeUser}
                      isMatch={is_match}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}

// Simplified Like Card component - no complex effects
function LikeCard({ like, likeUser, isUnlocked, unlockCost, onUnlock, onLikeBack }) {
  const navigate = useNavigate();
  const isBlurred = !isUnlocked;
  
  // Get photo URL
  const getPhotoUrl = () => {
    const photo = likeUser.main_photo || likeUser.photos?.[0] || likeUser.picture;
    if (!photo) return null;
    if (photo.startsWith("/api/photos/")) {
      return `${API}/photos/${photo.replace("/api/photos/", "")}`;
    }
    return photo;
  };
  
  const photoUrl = getPhotoUrl();
  
  return (
    <div className="bg-card/50 backdrop-blur-sm border border-white/5 rounded-2xl overflow-hidden">
      <div 
        className="relative aspect-[3/4] cursor-pointer"
        onClick={() => !isBlurred && navigate(`/user/${likeUser.user_id}`)}
      >
        {photoUrl ? (
          <img 
            src={photoUrl} 
            alt={isBlurred ? tx("Profil masqué") : likeUser.name}
            className={`w-full h-full object-cover ${isBlurred ? 'blur-xl scale-110' : ''}`}
            style={{ transition: 'filter 0.3s, transform 0.3s' }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/30 to-purple-600/30 flex items-center justify-center">
            <Lock className="w-12 h-12 text-white/30" />
          </div>
        )}
        
        {/* Blur overlay */}
        {isBlurred && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <div className="text-center">
              <Lock className="w-10 h-10 text-white/70 mx-auto mb-2" />
              <p className="text-white/70 text-sm font-medium">{tx("Profil masqué")}</p>
            </div>
          </div>
        )}
        
        {like.action_type === "super_like" && (
          <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-yellow-500 flex items-center justify-center">
            <Star className="w-4 h-4 text-black fill-current" />
          </div>
        )}
        
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />
        
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <h3 className="font-semibold text-white">
            {isBlurred ? "???" : likeUser.name}{likeUser.age ? `, ${likeUser.age}` : ''}
          </h3>
          {likeUser.location && (
            <div className="flex items-center gap-1 text-white/70 text-xs">
              <MapPin className="w-3 h-3" />
              <span>{isBlurred ? likeUser.location.split(",")[0] : likeUser.location}</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="p-3">
        {isBlurred ? (
          <button
            onClick={(e) => onUnlock(like.like_id, e.currentTarget)}
            className="w-full bg-gradient-to-r from-primary to-purple-600 text-white rounded-full font-bold py-2 px-4 text-sm flex items-center justify-center gap-1"
          >
            <Unlock className="w-4 h-4" />
            {tx("Débloquer (")}{unlockCost} {tx("crédits)")}
          </button>
        ) : (
          <Button
            onClick={onLikeBack}
            className="w-full bg-primary text-white rounded-full font-bold"
            size="sm"
          >
            <Heart className="w-4 h-4 mr-1 fill-current" />
            {tx("Liker en retour")}
          </Button>
        )}
      </div>
    </div>
  );
}

function SentLikeCard({ like, likeUser, isMatch }) {
  const navigate = useNavigate();
  
  const getPhotoUrl = () => {
    const photo = likeUser.main_photo || likeUser.photos?.[0] || likeUser.picture;
    if (!photo) return null;
    if (photo.startsWith("/api/photos/")) {
      return `${API}/photos/${photo.replace("/api/photos/", "")}`;
    }
    return photo;
  };
  
  const photoUrl = getPhotoUrl();
  
  return (
    <div className="bg-card/50 backdrop-blur-sm border border-white/5 rounded-2xl overflow-hidden">
      <div 
        className="relative aspect-[3/4] cursor-pointer"
        onClick={() => navigate(`/user/${likeUser.user_id}`)}
      >
        {photoUrl ? (
          <img 
            src={photoUrl} 
            alt={likeUser.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/30 to-purple-600/30 flex items-center justify-center">
            <span className="text-4xl text-white/50">{likeUser.name?.[0]}</span>
          </div>
        )}
        
        {like.action_type === "super_like" && (
          <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-yellow-500 flex items-center justify-center">
            <Star className="w-4 h-4 text-black fill-current" />
          </div>
        )}
        
        {isMatch && (
          <div className="absolute top-2 left-2 px-2 py-1 bg-green-500 rounded-full flex items-center gap-1">
            <Check className="w-3 h-3 text-white" />
            <span className="text-xs text-white font-bold">Match</span>
          </div>
        )}
        
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />
        
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <h3 className="font-semibold text-white">
            {likeUser.name}{likeUser.age ? `, ${likeUser.age}` : ''}
          </h3>
          {likeUser.location && (
            <div className="flex items-center gap-1 text-white/70 text-xs">
              <MapPin className="w-3 h-3" />
              <span>{likeUser.location}</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="p-3">
        {isMatch ? (
          <Button
            onClick={() => navigate(`/chat/${likeUser.user_id}`)}
            className="w-full bg-green-500 text-white rounded-full font-bold"
            size="sm"
          >
            <MessageCircle className="w-4 h-4 mr-1" />
            Message
          </Button>
        ) : (
          <p className="text-center text-xs text-muted-foreground py-1">
            {tx("En attente de réponse...")}
          </p>
        )}
      </div>
    </div>
  );
}
